import { Injectable } from '@angular/core';
import { HttpRequestDecorator } from '../core/triggerbee/http.request.decorator';
import { BehaviorSubject, Observable } from 'rxjs';
import { TriggerbeeApi } from 'app/core/triggerbee/triggerbee-api.enum';
import {
  Account,
  AccountCustomers,
  AccountsSearchResponse,
  AccountType,
  AssignableRole,
  CustomerMock,
  CustomersSearchResponse,
  CustomerSuccessManager,
  CustomProfileProperty,
  SearchAccountsRequest,
  SearchCustomersRequest,
  SearchUsersRequest,
  StaticAccountData,
  UsersAdmin,
  UsersSearchResponse,
} from './account.model';
import { Currency } from '../shared/account/Currency';
import { CustomerItem } from '../layout/customer-selector/customer-item';
import { GoalModel } from 'app/account-settings/models/events.model';
import { PatchRequest } from 'app/shared/patch-request.model';

@Injectable({
  providedIn: 'root',
})
export class AccountAdminService {
  public userBackOfficeBS = new BehaviorSubject<boolean>(null);
  /* customersMock: Array<CustomerMock> = [
    {
      name: "group2",
      id: "1234"
    },
    {
      name: "hfghfhf",
      id: "1235"
    },
    {
      name: "group1111",
      id: "1236"
    }
  ]; */

  staticProfileFields = [
    {
      name: 'Firstname',
      mapTo: 'firstname',
      mapFrom: 'firstname',
      readonly: true,
      isAccountIdentifier: false,
    },
    {
      name: 'Lastname',
      mapTo: 'lastname',
      mapFrom: 'lastname',
      readonly: true,
      isAccountIdentifier: false,
    },
    {
      name: 'Email',
      mapTo: 'email',
      mapFrom: 'email',
      readonly: true,
      isAccountIdentifier: true,
    },
    {
      name: 'Telephone',
      mapTo: 'telephone',
      mapFrom: 'telephone',
      readonly: true,
      isAccountIdentifier: true,
    },
    {
      name: 'Organization',
      mapTo: 'organization',
      mapFrom: 'organization',
      readonly: true,
      isAccountIdentifier: false,
    },
  ];
  staticAccountMock: StaticAccountData = {
    accountTypes: Object.keys(AccountType),
    planTypes: ['EngageBasic', 'EngagePlus', 'Leads'],
    currencies: [
      Currency.Eur,
      Currency.Sek,
      Currency.Usd,
      Currency.Nok,
      Currency.Jpy,
      Currency.Cad,
      Currency.Chf,
      Currency.Brl,
      Currency.Dkk,
      Currency.Gbp,
      Currency.Aud,
      Currency.Cny,
      Currency.Inr,
      Currency.Mxn,
      Currency.Pln,
      Currency.Hkd,
      Currency.Krw,
      Currency.Sgd,
      Currency.Try,
      Currency.Zar,
      Currency.Rub,
      Currency.Nzd,
    ],
    planSizes: ['Small', 'Medium', 'Large', 'Custom'],
    personalDataFields: ['phone', 'email', ''],
    ipDbs: ['MaxMind', 'DigitalElements', 'IPRegistry'],
    //TODO: get right plan size values
    planSizeValues: {
      small: {
        maxContacts: 1000,
        maxBatchRecordsPerMonth: 1000,
        maxPageviewsPerMonth: 10000,
      },
      medium: {
        maxContacts: 10000,
        maxBatchRecordsPerMonth: 10000,
        maxPageviewsPerMonth: 100000,
      },
      big: {
        maxContacts: 50000,
        maxBatchRecordsPerMonth: 50000,
        maxPageviewsPerMonth: 500000,
      },
    },
  };

  constructor(private http: HttpRequestDecorator) {}

  public getAllAccounts(): Observable<Array<Account>> {
    return this.http.get(this.getAccountResource('admin/accounts'));
  }

  public getAccount(siteId): Observable<Account> {
    return this.http.get(this.getAccountResource(`admin/accounts/${siteId}`));
  }

  public patchAccount(
    siteId: number,
    properties: { propertyName: string; value: any }[],
    customerId?: string,
  ): Observable<Account> {
    let body;
    if (properties.length) {
      body = properties.map((p) => ({
        op: 'add',
        path: `/${p.propertyName}`,
        value: p.value,
      }));
    }
    return this.http.patch(this.getAccountResource(`admin/accounts/${siteId}?customerId=${customerId || ''}`), body);
  }

  public postAccount(account): Observable<Account> {
    return this.http.post(this.getAccountResource(`admin/accounts/`), account);
  }

  /* public getCustomers(): Observable<Array<CustomerMock>> {
    return new Observable<Array<CustomerMock>>(subscriber => {
      subscriber.next(this.customersMock);
    });
  } */
  public getCustomers(): Observable<Array<CustomerMock>> {
    return this.http.get(this.getAccountResource(`admin/customers`));
  }

  public getCustomersForDropdown(siteIds: number[]): Observable<Array<CustomerItem>> {
    return this.http.post(this.getAccountResource(`admin/customers/dropdown`), { siteIds });
  }

  /** Server-paged. The response carries the match count, the stat tiles and the filter options. */
  public searchCustomers(request: SearchCustomersRequest): Observable<CustomersSearchResponse> {
    return this.http.post(this.getAccountResource(`admin/customers/search`), request);
  }

  /** Server-paged. */
  public searchAccountsPaged(request: SearchAccountsRequest): Observable<AccountsSearchResponse> {
    return this.http.post(this.getAccountResource(`admin/accounts/v2/search`), request);
  }

  public searchUsers(request: SearchUsersRequest): Observable<UsersSearchResponse> {
    return this.http.post(this.getAccountResource(`admin/users/search`), request);
  }

  public getCustomerLimitation(id): Observable<AccountCustomers> {
    return this.http.get(this.getAccountResource(`admin/customers/${id}`));
  }

  public deleteCustomerWithAccounts(
    id: string,
    operations: Array<{ path: string; value: string | number | boolean }>,
  ): Observable<Array<CustomerMock>> {
    const body = operations.map((op) => ({
      path: op.path,
      op: 'replace',
      value: op.value,
    }));
    return this.http.patch(this.getAccountResource(`admin/customers/${id}`), body);
  }

  public deleteAccount(id: string, isDelete: null | string): Observable<Account> {
    return this.http.put(this.getAccountResource(`admin/accounts/${id}/delete-action`), { deleteAction: isDelete });
  }

  public updateAccountsOnCustomer(
    id: string,
    operations: Array<{ path: string; value: string | number }>,
  ): Observable<Account> {
    const body = operations.map((op) => ({
      path: op.path,
      op: 'replace',
      value: op.value,
    }));
    return this.http.patch(this.getAccountResource(`admin/accounts/customers/${id}`), body);
  }

  public getCustomProfileProperties(): Observable<Array<CustomProfileProperty>> {
    return this.http.get(this.getAccountResource(`custom-profile-properties`));
  }

  public createCustomer(customer): Observable<CustomerMock> {
    return this.http.post(this.getAccountResource(`admin/customers`), customer);
  }

  public updateCustomer(id: string, customerLimitation: any): Observable<AccountCustomers> {
    let body = [];
    const modifyChangedProperties = [];
    for (const key in customerLimitation) {
      modifyChangedProperties.push({
        propertyName: key,
        value: customerLimitation[key],
      });
    }
    if (modifyChangedProperties.length) {
      body = modifyChangedProperties.map((p) => ({
        op: 'replace',
        path: `/${p.propertyName}`,
        value: p.value,
      }));
    }
    return this.http.patch(this.getAccountResource(`admin/customers/${id}`), body);
  }

  public getStaticAccountData(): Observable<StaticAccountData> {
    const staticAccount = new Observable<StaticAccountData>((subscriber) => {
      subscriber.next(this.staticAccountMock);
    });
    return staticAccount;
  }

  private getAccountResource(subResource?: string): string {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/` + (subResource || '');
  }

  public getIpdbTranslationKey(key): string {
    if (key === 'MaxMind') {
      return 'account-admin.max-mind';
    } else if (key === 'DigitalElements') {
      return 'account-admin.digital-elements';
    } else if (key === 'IPRegistry') {
      return 'account-admin.ip-registry';
    } else {
      return '';
    }
  }

  public calculateVisitsTimeToLive(days: number): number {
    return 86400 * days;
  }

  public calculateDaysFromTimeToLive(ttl: number): number {
    if (!ttl) {
      return null;
    }
    return ttl / 86400;
  }

  public getAllUsers(): Observable<Array<UsersAdmin>> {
    return this.http.get(this.getAccountResource('admin/users'));
  }

  public getDetailUser(email: string): Observable<UsersAdmin> {
    return this.http.get(this.getAccountResource('admin/users/' + email));
  }

  public deleteUser(email: string): Observable<any> {
    return this.http.delete(this.getAccountResource('admin/users/' + email));
  }

  public patchUser(userId: string, operations: PatchRequest[]): Observable<UsersAdmin> {
    return this.http.patch(this.getAccountResource('admin/users/' + userId), operations);
  }

  public getAssignableRoles(): Observable<AssignableRole[]> {
    return this.http.get(this.getAccountResource('admin/users/roles'));
  }

  public getCustomerSuccessManagers(): Observable<CustomerSuccessManager[]> {
    return this.http.get(this.getAccountResource('admin/users/customer-success'));
  }

  public createUser(user: UsersAdmin): Observable<UsersAdmin> {
    const email = user.email;
    delete user.email;
    return this.http.post(this.getAccountResource('admin/users/' + email), user);
  }

  public getGoalsForAccount(siteId: number): Observable<GoalModel[]> {
    return this.http.get(this.getAccountResource(`admin/accounts/${siteId}/goals`));
  }
}
